import { supabase } from "@/integrations/supabase/client";
import { optimizeImage, AVATAR_MAX_SIDE } from "@/lib/optimizeImage";

/** Formats de photo acceptés avant recadrage. */
const ALLOWED_AVATAR_TYPES = ["image/jpeg", "image/png", "image/webp"];
/** Limite par défaut (Mo) si le réglage admin est absent. */
export const DEFAULT_AVATAR_MAX_MB = 5;

/** Lit la limite admin, avec repli sur 5 Mo. */
export const getAvatarMaxMb = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : DEFAULT_AVATAR_MAX_MB;
};

/** Vérifie le fichier choisi : renvoie un message d'erreur, ou null s'il est valide. */
export const validateAvatarSource = (file: File, maxMb: number): string | null => {
  if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
    return "Formats acceptés : JPG, PNG ou WEBP.";
  }
  if (file.size > maxMb * 1024 * 1024) {
    return `La photo ne doit pas dépasser ${maxMb} Mo.`;
  }
  return null;
};

/**
 * Envoie le carré recadré (JPEG) dans le bucket avatars.
 * Le premier segment du chemin DOIT être l'uid : la policy RLS du bucket
 * contrôle (storage.foldername(name))[1] = auth.uid().
 */
export const uploadCroppedAvatar = async (
  userId: string,
  blob: Blob
): Promise<{ publicUrl: string; path: string }> => {
  const ts = Date.now();
  const file = await optimizeImage(blob, { maxSide: AVATAR_MAX_SIDE, baseName: `avatar-${ts}` });
  const path = `${userId}/${file.name}`;

  const { error } = await supabase.storage
    .from("avatars")
    .upload(path, file, { contentType: file.type, upsert: true });
  if (error) throw error;

  const {
    data: { publicUrl },
  } = supabase.storage.from("avatars").getPublicUrl(path);
  return { publicUrl, path };
};

/** Chemin de stockage d'une URL publique du bucket avatars (null si hors bucket). */
export const avatarPathFromUrl = (url: string | null | undefined): string | null => {
  if (!url) return null;
  const marker = "/storage/v1/object/public/avatars/";
  const i = url.indexOf(marker);
  return i === -1 ? null : decodeURIComponent(url.slice(i + marker.length).split("?")[0]);
};

/** Supprime les autres fichiers du dossier de l'utilisateur. Non bloquant. */
export const removeObsoleteAvatars = async (userId: string, keepPath: string | null) => {
  try {
    const { data: previous, error } = await supabase.storage.from("avatars").list(userId);
    if (error) throw error;
    const obsolete = (previous ?? [])
      .map((f) => `${userId}/${f.name}`)
      .filter((p) => p !== keepPath);
    if (obsolete.length > 0) {
      await supabase.storage.from("avatars").remove(obsolete);
    }
  } catch (error) {
    // La nouvelle photo est déjà en place : on journalise sans bloquer.
    console.error("Avatar cleanup failed", error);
  }
};
